import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, Mic } from 'lucide-react';
import { voiceInfraApi } from '../../pages/voice-infrastructure/voiceInfraApi';

type PlatformSnap = {
  workspace_id: string;
  platform_code: string;
  platform_label: string;
  allowed_platform_codes?: string[];
  configured?: boolean;
};

type ValidationCheck = { name: string; ok: boolean; detail?: string };
type ValidationData = {
  ok: boolean;
  checks?: ValidationCheck[];
  reject_reasons?: string[];
};

const PLATFORM_OPTIONS = [
  { code: 'retell', label: 'Retell' },
  { code: 'livekit', label: 'SWS AI Calling' },
];

interface WorkspaceVoicePlatformCardProps {
  workspaceId: string;
  workspaceName?: string;
  compact?: boolean;
}

export const WorkspaceVoicePlatformCard: React.FC<WorkspaceVoicePlatformCardProps> = ({
  workspaceId,
  workspaceName,
  compact = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [platform, setPlatform] = useState<PlatformSnap | null>(null);
  const [selected, setSelected] = useState('retell');
  const [reason, setReason] = useState('');
  const [validation, setValidation] = useState<ValidationData | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await voiceInfraApi.workspaceVoicePlatform(workspaceId);
      const snap = (res?.data ?? res) as PlatformSnap;
      setPlatform(snap);
      setSelected(snap.platform_code || 'retell');
    } catch (e: any) {
      setError(e?.message || 'Failed to load voice platform');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runValidate = async () => {
    if (selected === 'retell') {
      setValidation({ ok: true, checks: [] });
      return;
    }
    setValidation(null);
    try {
      const res: any = await voiceInfraApi.validateWorkspaceVoicePlatform(workspaceId, {
        platform_code: selected,
      });
      setValidation((res?.data ?? res) as ValidationData);
    } catch (e: any) {
      const detail = e?.detail || e?.response?.data?.detail;
      if (detail?.validation) {
        setValidation(detail.validation as ValidationData);
        setError(detail.message || 'Validation failed');
      } else {
        setError(e?.message || 'Validation failed');
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    setValidation(null);
    try {
      const res: any = await voiceInfraApi.setWorkspaceVoicePlatform(workspaceId, {
        platform_code: selected,
        reason: reason.trim() || undefined,
      });
      const snap = res?.platform as PlatformSnap;
      if (snap) setPlatform(snap);
      setSuccess(
        res?.changed
          ? `Voice Platform updated to ${snap?.platform_label || selected}`
          : 'No change — platform already set'
      );
      setReason('');
    } catch (e: any) {
      const detail = e?.detail || e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.validation) {
        setValidation(detail.validation as ValidationData);
        setError(detail.message || 'Could not switch platform');
      } else {
        setError(typeof detail === 'string' ? detail : e?.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-enterprise-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading voice platform…
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'rounded-xl border border-enterprise-200 bg-white p-5 shadow-sm'}>
      {!compact && (
        <h3 className="flex items-center gap-2 text-sm font-semibold text-enterprise-900">
          <Mic className="h-4 w-4 text-enterprise-500" />
          Voice Platform
        </h3>
      )}
      {workspaceName && !compact && (
        <p className="mt-1 text-xs text-enterprise-500">{workspaceName}</p>
      )}

      <div className={compact ? 'space-y-3' : 'mt-4 space-y-3'}>
        <div>
          <label className="text-xs font-medium text-enterprise-700">Active platform</label>
          <p className="text-sm text-enterprise-600">
            Current:{' '}
            <span className="font-semibold text-enterprise-900">
              {platform?.platform_label || 'Retell'}
            </span>
          </p>
        </div>

        <div>
          <label htmlFor={`vp-${workspaceId}`} className="text-xs font-medium text-enterprise-700">
            Voice Platform
          </label>
          <select
            id={`vp-${workspaceId}`}
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              setValidation(null);
              setError(null);
            }}
            className="mt-1 w-full rounded-lg border border-enterprise-200 px-3 py-2 text-sm"
          >
            {PLATFORM_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-enterprise-500">
            Affects new AI calls only. Existing active calls are unchanged.
          </p>
        </div>

        <div>
          <label className="text-xs font-medium text-enterprise-700">Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Rollback to Retell for production stability"
            className="mt-1 w-full rounded-lg border border-enterprise-200 px-3 py-2 text-sm"
          />
        </div>

        {selected === 'livekit' && (
          <button
            type="button"
            onClick={() => void runValidate()}
            className="text-xs font-medium text-sky-700 hover:underline"
          >
            Run SWS preflight validation
          </button>
        )}

        {validation && (
          <div
            className={`rounded-lg border p-3 text-xs ${
              validation.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}
          >
            <div className="flex items-center gap-1.5 font-semibold">
              {validation.ok ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5" />
              )}
              {validation.ok ? 'SWS AI Calling ready' : 'SWS validation failed'}
            </div>
            {(validation.checks || []).map((c) => (
              <div key={c.name} className="mt-1 flex gap-2">
                <span>{c.ok ? '✓' : '✗'}</span>
                <span>
                  {c.name}
                  {c.detail ? ` — ${c.detail}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-xs text-rose-600">{error}</p>}
        {success && <p className="text-xs text-emerald-700">{success}</p>}

        <button
          type="button"
          disabled={saving}
          onClick={() => void handleSave()}
          className="rounded-lg bg-enterprise-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Voice Platform'}
        </button>
      </div>
    </div>
  );
};
